import { activeProgramFromWire } from '../utils/programEnrollment';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { V, Haptic } from '../utils/theme';
import { appName } from '../utils/brand';
import { LS } from '../utils/storage';
import { Icons } from '../components/Icons';
import { Btn, Field, Sheet, SuccessToastCtrl } from '../components/ui';
import { uid, today, isIOS as detectIOS, isStandalone as detectStandalone } from '../utils/helpers';
import { CloudSync, SYNC_URL } from '../utils/sync';
import { AuthToken, SessionManager } from '../utils/auth';
import { apiPost, ApiError } from '../utils/api';
import { SentryUtil } from '../utils/sentry';
import { defaultExercises } from '../data/exercises';
import { US_STATES } from '../data/usStates';
import { defaultSchedule } from '../state/reducer';
import { TOSContent, PrivacyContent } from './SettingsTab';
import { calcEnergy } from './BodyTab';
import { useLayout } from '../utils/responsive';
import {
  LogoMark, Wordmark, Eyebrow, StepHeader, SectionLabel, ProgressSteps, OptionCard, Segmented, ChipRow, MultiChipRow,
  TextInput, SelectField, CodeInput, Notice, Callout, LinkBtn, FeatureTile, WeekStrip, AppPreview, InstallGuide,
} from './onboarding/ui';

// ─── Static content ───
const STEP_LABELS = ["Units", "Goal", "Schedule", "Body", "Training", "Profile", "PIN"];

const SESSION_LENGTHS = [30, 45, 60, 90];
const TRAINING_TIMES = ["Morning", "Midday", "Evening", "Flexible"];
const EQUIPMENT = [
  { id: "gym", label: "Full gym", desc: "Barbells, racks, machines", icon: Icons.dumbbell, color: null },
  { id: "home", label: "Home setup", desc: "Dumbbells, bands, a bench", icon: Icons.target, color: "accent2" },
  { id: "bodyweight", label: "Bodyweight", desc: "No equipment for now", icon: Icons.activity, color: "warn" },
  { id: "machines", label: "Machines only", desc: "Hotel or apartment gym", icon: Icons.sliders, color: "purple" },
];
// Joint ids match InjuryManager / JOINT_MAP in features/coaching.jsx
const JOINTS = [
  { id: "shoulder", label: "Shoulder" }, { id: "knee", label: "Knee" }, { id: "lower_back", label: "Lower back" },
  { id: "elbow", label: "Elbow" }, { id: "wrist", label: "Wrist" }, { id: "hip", label: "Hip" },
];
const DIETS = ["No preference", "Vegetarian", "Vegan", "Pescatarian", "Halal", "Kosher"];
const ALLERGIES = ["Dairy", "Gluten", "Nuts", "Eggs", "Shellfish", "Soy"];

// Which weekdays get a session for N days/week (0 = Sunday)
const DAY_SLOTS = { 2: [1, 4], 3: [1, 3, 5], 4: [1, 2, 4, 5], 5: [1, 2, 3, 4, 5], 6: [1, 2, 3, 4, 5, 6], 7: [1, 2, 3, 4, 5, 6, 0] };
const SPLIT_CYCLES = { PPL: ["Push", "Pull", "Legs"], Bro: ["Chest", "Back", "Shoulders", "Arms", "Legs"], "Upper/Lower": ["Upper", "Lower"], "Full Body": ["Full Body"] };
// Build a weekly schedule for a split and a number of training days
const buildWeek = (split, days) => {
  const week = { 0: "Rest", 1: "Rest", 2: "Rest", 3: "Rest", 4: "Rest", 5: "Rest", 6: "Rest" };
  const cycle = SPLIT_CYCLES[split];
  if (!cycle) return week;
  (DAY_SLOTS[days] || DAY_SLOTS[4]).forEach((dow, i) => { week[dow] = cycle[i % cycle.length]; });
  return week;
};
// Pick a split from availability and experience
const recommendSplit = (days, level) => {
  if (days <= 3) return "Full Body";
  if (days === 4) return "Upper/Lower";
  return level === "Beginner" ? "Upper/Lower" : "PPL";
};
// Calorie adjustment from maintenance, by primary goal
const GOAL_ADJUST = { "Lose Fat": -500, "Build Muscle": 250, "Body Recomposition": 0, "Strength": 150, "Maintain Fitness": 0 };
const round5 = (n) => Math.round(n / 5) * 5;

const GOALS = [
  { id: "Build Muscle", icon: Icons.dumbbell, color: null, desc: "Add size and strength with progressive overload" },
  { id: "Lose Fat", icon: Icons.flame, color: "warn", desc: "Cut body fat while keeping your lifts" },
  { id: "Body Recomposition", icon: Icons.scale, color: "accent2", desc: "Lose fat and gain muscle at the same time" },
  { id: "Strength", icon: Icons.trophy, color: "purple", desc: "Maximise your big lifts and chase PRs" },
  { id: "Maintain Fitness", icon: Icons.target, color: null, desc: "Stay consistent, healthy and injury-free" },
];

const FEATURES = [
  { icon: Icons.dumbbell, color: "accent", title: "Workout logging", desc: "250+ exercises, rest timer, plate calculator and automatic PR detection." },
  { icon: Icons.fork, color: "warn", title: "Nutrition & macros", desc: "350+ foods, barcode scanning and daily macro rings." },
  { icon: Icons.chart, color: "accent2", title: "Analytics", desc: "Strength curves, weekly volume and a muscle heat map." },
  { icon: Icons.trophy, color: "purple", title: "Social & streaks", desc: "Friends, duels, weekly wars and XP for every session." },
];

const TRUST = ["Works offline", "No ads", "Free forever", "Your data stays yours"];

// Full-screen frame used by the sign-in and verification screens
const AuthShell = ({ children, onBack, backLabel }) => (
  <div style={{ height: "100%", overflowY: "auto", WebkitOverflowScrolling: "touch", background: `${V.ambient}, ${V.bg}`, color: V.text, fontFamily: V.font }}>
    <div style={{ minHeight: "100%", display: "flex", flexDirection: "column", maxWidth: 440, margin: "0 auto",
      padding: "max(12px, env(safe-area-inset-top)) 24px max(24px, env(safe-area-inset-bottom))" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 48 }}>
        {onBack ? (
          <button type="button" onClick={onBack} style={{ background: "none", border: "none", display: "flex", alignItems: "center", gap: 4,
            padding: "8px 0", cursor: "pointer", fontFamily: V.font, WebkitTapHighlightColor: "transparent" }}>
            {Icons.chevLeft({ size: 18, color: V.accent })}
            <span style={{ fontSize: 14, color: V.accent, fontWeight: 600 }}>{backLabel || "Back"}</span>
          </button>
        ) : <span />}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}><LogoMark size={26} /><Wordmark size={16} /></div>
      </div>
      <div className="tab-anim" style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", padding: "24px 0" }}>
        {children}
      </div>
    </div>
  </div>
);

const AuthTitle = ({ icon, iconColor, title, children }) => (
  <div style={{ textAlign: "center", marginBottom: 24 }}>
    {icon && <div style={{ display: "inline-flex", width: 64, height: 64, borderRadius: 20, alignItems: "center", justifyContent: "center", marginBottom: 18,
      background: `linear-gradient(135deg,${iconColor || V.accent}26,${iconColor || V.accent}0a)`, border: `1px solid ${iconColor || V.accent}30` }}>
      {icon({ size: 28, color: iconColor || V.accent, strokeWidth: 2.2 })}
    </div>}
    <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, letterSpacing: "-0.02em", color: V.text }}>{title}</h1>
    {children && <div style={{ fontSize: 14, color: V.text2, marginTop: 8, lineHeight: 1.55 }}>{children}</div>}
  </div>
);

const tone = (name) => (name ? V[name] : V.accent);

export function Onboarding({d,initialSignIn}){
  const { isMobile } = useLayout();
  // Track mount + pending timers so async callbacks don't fire after unmount
  const _mountedRef=useRef(true);
  const _timersRef=useRef([]);
  useEffect(()=>()=>{_mountedRef.current=false;_timersRef.current.forEach(clearInterval);_timersRef.current.forEach(clearTimeout);_timersRef.current=[];},[]);
  const [step,setStep]=useState(0);
  const [split,setSplit]=useState("PPL");
  const [units,setUnits]=useState("lbs");
  const [goals,setGoals]=useState({cal:"2400",protein:"180",carbs:"250",fat:"70",goalWeight:""});
  const [bw,setBw]=useState("");
  const [sending,setSending]=useState(false);
  const [agreed,setAgreed]=useState(false);
  const [showLegal,setShowLegal]=useState(null);
  const [accountPin,setAccountPin]=useState("");
  const [confirmPin,setConfirmPin]=useState("");
  const [pinError,setPinError]=useState("");
  const [primaryGoal,setPrimaryGoal]=useState("");
  const [profile,setProfile]=useState({
    firstName:"",lastName:"",nickname:"",email:"",dob:"",sex:"",state:"",city:"",zipCode:"",
    height:"",fitnessLevel:"",activityLevel:"Moderate",weeklyAvailability:""
  });
  // Schedule
  const [trainingDays,setTrainingDays]=useState(4);
  const [sessionLength,setSessionLength]=useState(60);
  const [trainingTime,setTrainingTime]=useState("");
  const [splitTouched,setSplitTouched]=useState(false); // user picked a split other than the recommendation
  const [showSplits,setShowSplits]=useState(false);
  // Body
  const [heightFt,setHeightFt]=useState("");
  const [heightIn,setHeightIn]=useState("");
  const [heightCm,setHeightCm]=useState("");
  const [targetsTouched,setTargetsTouched]=useState(false); // user edited the suggested targets
  // Training
  const [equipment,setEquipment]=useState("");
  const [lifts,setLifts]=useState({bench:"",squat:"",deadlift:""});
  const [injuries,setInjuries]=useState([]); // joint ids
  // Food
  const [diet,setDiet]=useState("No preference");
  const [allergies,setAllergies]=useState([]);

  // ── Derived: split recommendation, height string, suggested targets ──
  const recommendedSplit=recommendSplit(trainingDays,profile.fitnessLevel);
  useEffect(()=>{if(!splitTouched)setSplit(recommendedSplit);},[recommendedSplit,splitTouched]);
  const weekPlan=useMemo(()=>buildWeek(split,trainingDays),[split,trainingDays]);
  // Height in the string format the rest of the app parses (parseHeightToM): 5'10 or 178 cm
  const heightStr=units==="kg"?(heightCm?`${heightCm} cm`:""):(heightFt?`${heightFt}'${heightIn||0}`:"");
  const heightCmNum=units==="kg"?(parseFloat(heightCm)||null):(heightFt?Math.round((parseInt(heightFt)*12+(parseInt(heightIn)||0))*2.54):null);
  const suggested=useMemo(()=>{
    const e=calcEnergy({dob:profile.dob,sex:profile.sex,activityLevel:profile.activityLevel,height:heightStr},bw,units);
    if(!e)return null;
    const adjust=GOAL_ADJUST[primaryGoal]??0;
    const cal=Math.max(1200,round5(e.tdee+adjust));
    const wLb=units==="kg"?parseFloat(bw)*2.20462:parseFloat(bw);
    const perLb=primaryGoal==="Lose Fat"?1.0:primaryGoal==="Build Muscle"||primaryGoal==="Strength"?0.9:0.8;
    const protein=round5(wLb*perLb);
    const fat=round5(cal*0.25/9);
    const carbs=Math.max(0,round5((cal-protein*4-fat*9)/4));
    return{tdee:e.tdee,bmr:e.bmr,adjust,cal,protein,carbs,fat,perLb};
  },[profile.dob,profile.sex,profile.activityLevel,heightStr,bw,units,primaryGoal]);
  const applySuggested=()=>{if(!suggested)return;setGoals(g=>({...g,cal:String(suggested.cal),protein:String(suggested.protein),carbs:String(suggested.carbs),fat:String(suggested.fat)}));setTargetsTouched(false);};
  useEffect(()=>{if(suggested&&!targetsTouched)setGoals(g=>({...g,cal:String(suggested.cal),protein:String(suggested.protein),carbs:String(suggested.carbs),fat:String(suggested.fat)}));},[suggested,targetsTouched]);
  const editGoal=(k)=>(v)=>{setTargetsTouched(true);setGoals(g=>({...g,[k]:v}));};

  const splits={
    "PPL":{desc:"Push / Pull / Legs",days:{1:"Push",2:"Pull",3:"Legs",4:"Push",5:"Pull",6:"Legs",0:"Rest"}},
    "Bro":{desc:"Chest / Back / Shoulders / Arms / Legs",days:{1:"Chest",2:"Back",3:"Shoulders",4:"Arms",5:"Legs",6:"Cardio",0:"Rest"}},
    "Upper/Lower":{desc:"Upper / Lower / Rest / Repeat",days:{1:"Upper",2:"Lower",3:"Rest",4:"Upper",5:"Lower",6:"Cardio",0:"Rest"}},
    "Full Body":{desc:"3x per week full body",days:{1:"Full Body",2:"Rest",3:"Full Body",4:"Rest",5:"Full Body",6:"Cardio",0:"Rest"}},
    "Custom":{desc:"Set your own schedule later",days:{0:"Rest",1:"Rest",2:"Rest",3:"Rest",4:"Rest",5:"Rest",6:"Rest"}},
  };

  const fetchWithTimeout=(url,opts,ms=10000)=>{
    const ctrl=new AbortController();
    const timer=setTimeout(()=>ctrl.abort(),ms);
    return fetch(url,{...opts,signal:ctrl.signal}).finally(()=>clearTimeout(timer));
  };

  const sendToBackend=async(payload)=>{
    try{
      const res=await fetchWithTimeout(`${SYNC_URL}/api/users`,{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify(payload),
      });
      if(!res.ok)throw new Error("Server error");
      // Returns the created user row (friend_code etc.) so the profile can keep the
      // server-issued friend code — the only code add-by-code will match.
      return await res.json().catch(()=>({}))||{};
    }catch(e){
      // Silently fail — data is stored locally regardless
      // sync deferred;
      return null;
    }
  };

  const finish=async()=>{
    // Validate PIN
    if(accountPin.length!==6){setPinError("PIN must be exactly 6 digits");return;}
    if(accountPin!==confirmPin){setPinError("PINs don't match");return;}
    setPinError("");
    setSending(true);

    try{
    // Store account PIN locally, encrypted at rest (AES-GCM via LS.setSecure).
    // The server-side PIN check needs the raw value during sync, so it stays
    // on-device — but never in cleartext. If SubtleCrypto is unavailable the
    // write is skipped and restore flows simply prompt for the PIN instead.
    await LS.setSecure("ft-account-pin",accountPin);

    // Build full payload
    const liftsNum={bench:parseFloat(lifts.bench)||0,squat:parseFloat(lifts.squat)||0,deadlift:parseFloat(lifts.deadlift)||0,units};
    const hasLifts=liftsNum.bench||liftsNum.squat||liftsNum.deadlift;
    // Everything the wizard learned beyond the classic users columns, kept together so the
    // server can store it as one JSON blob and restore it on a new device.
    const preferences={
      heightCm:heightCmNum,sessionLength,trainingTime:trainingTime||null,equipment:equipment||null,
      lifts:hasLifts?liftsNum:null,injuries,diet:diet==="No preference"?null:diet,allergies,
      tdee:suggested?.tdee||null,
    };
    const fullProfile={...profile,height:heightStr,weeklyAvailability:trainingDays,
      sessionLength,trainingTime:trainingTime||null,equipment:equipment||null,lifts:hasLifts?liftsNum:null,
      diet:diet==="No preference"?null:diet,allergies};
    const payload={
      ...profile,
      height:heightStr,
      weeklyAvailability:String(trainingDays),
      preferences,
      currentWeight:parseFloat(bw)||null,
      targetWeight:parseFloat(goals.goalWeight)||null,
      units,split,primaryGoal,
      goals:{cal:parseInt(goals.cal),protein:parseInt(goals.protein),carbs:parseInt(goals.carbs),fat:parseInt(goals.fat),tdee:suggested?.tdee||0},
      accountPin:accountPin,
      createdAt:new Date().toISOString(),
      consentedAt:new Date().toISOString(),
      consentVersion:"1.0",
      deviceId:uid(),
      app:"ironlog",
    };

    // Send to backend (non-blocking — app works regardless)
    const created=await sendToBackend(payload);
    if(created?.friend_code)fullProfile.friendCode=created.friend_code;

    // Apply to local state
    d({type:"UNITS",u:units});
    d({type:"GOALS",g:{cal:parseInt(goals.cal)||2400,protein:parseInt(goals.protein)||180,
      carbs:parseInt(goals.carbs)||250,fat:parseInt(goals.fat)||70,goalWeight:parseFloat(goals.goalWeight)||(units==="kg"?80:175),
      tdee:suggested?.tdee||0}});
    d({type:"SET_SCHEDULE",schedule:{weekly:buildWeek(split,trainingDays),overrides:{}}});
    d({type:"SET_PROFILE",profile:fullProfile});
    if(injuries.length)d({type:"SET_INJURIES",injuries:injuries.map(j=>({id:uid(),joint:j,severity:"moderate",notes:"Flagged during setup",date:today(),active:true}))});
    if(bw){d({type:"ADD_B",b:{id:uid(),date:today(),weight:parseFloat(bw)||0,bf:0,waist:0,hips:0,chest:0,neck:0,arms:0,thighs:0,calves:0}});}
    // Init auth key for this device
    AuthToken.init(profile.email);
    LS.set("ft-device-id",payload.deviceId);
    // Create server-issued session token
    await SessionManager.create(profile.email,accountPin,payload.deviceId);
    // Send email verification code then show verify screen
    try{
      await fetchWithTimeout(`${SYNC_URL}/api/auth/session`,{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({action:"send_verify_code",email:profile.email})});
    }catch(e){}
    }finally{
    if(_mountedRef.current){
      setVerifyStep("pending");
      setSending(false);
    }
    }
    // Note: ONBOARDED is dispatched after email verified (or skipped via skip)
  };

  // ─── Onboarding Validation ───
  const BLOCKED_WORDS=["fuck","shit","ass","bitch","dick","pussy","cunt","damn","cock","porn",
    "nigger","nigga","faggot","retard","whore","slut","bastard","penis","vagina","nazi","kill","rape"];
  // Word-boundary match so "Scunthorpe", "Cassie", "Damaris", "Assad" don't get flagged.
  // Non-letters become spaces so the boundary can match them.
  const _BLOCKED_RE = new RegExp(`\\b(${BLOCKED_WORDS.join("|")})\\b`,"i");
  const containsProfanity=(text)=>{
    const normalized=(text||"").toLowerCase().replace(/[^a-z]+/g," ");
    return _BLOCKED_RE.test(normalized);
  };
  const isValidEmail=(email)=>/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
  const getAge=(dob)=>{
    if(!dob)return 0;
    const birth=new Date(dob+"T00:00:00");
    const now=new Date();
    let age=now.getFullYear()-birth.getFullYear();
    if(now.getMonth()<birth.getMonth()||(now.getMonth()===birth.getMonth()&&now.getDate()<birth.getDate()))age--;
    return age;
  };

  const nameClean=!containsProfanity(profile.firstName)&&!containsProfanity(profile.lastName)&&!containsProfanity(profile.nickname);
  const emailValid=isValidEmail(profile.email);
  const ageValid=getAge(profile.dob)>=16;
  const bodyValid=!!(profile.sex&&profile.dob&&ageValid);
  const profileValid=profile.firstName.trim()&&profile.lastName.trim()&&profile.email.trim()&&profile.dob&&profile.sex&&profile.state&&agreed&&nameClean&&emailValid&&ageValid;

  const getValidationMsg=()=>{
    if(!profile.firstName.trim()||!profile.lastName.trim()||!profile.email.trim()||!profile.dob||!profile.sex||!profile.state)return"Fill in the required fields (*) to continue";
    if(!nameClean)return"Please use appropriate names";
    if(!emailValid)return"Please enter a valid email address";
    if(!ageValid)return"You must be at least 16 years old to use IRONLOG";
    if(!agreed)return"Please agree to the Terms of Service and Privacy Policy";
    return"";
  };

  const [showSignIn,setShowSignIn]=useState(!!initialSignIn);
  const [signInEmail,setSignInEmail]=useState("");
  const [signInPin,setSignInPin]=useState("");
  const [signInLoading,setSignInLoading]=useState(false);
  const [signInError,setSignInError]=useState("");
  const [signInStep,setSignInStep]=useState("email"); // email | pin | forgot | forgot_code | forgot_newpin

  // ─── Forgot PIN state ───
  const [forgotEmail,setForgotEmail]=useState("");
  const [forgotCode,setForgotCode]=useState("");
  const [forgotNewPin,setForgotNewPin]=useState("");
  const [forgotConfirmPin,setForgotConfirmPin]=useState("");
  const [forgotResetToken,setForgotResetToken]=useState("");
  const [forgotLoading,setForgotLoading]=useState(false);
  const [forgotError,setForgotError]=useState("");
  const [forgotSuccess,setForgotSuccess]=useState("");

  const forgotSendCode=async()=>{
    if(!forgotEmail.trim()||!forgotEmail.includes("@")){setForgotError("Enter a valid email");return;}
    setForgotLoading(true);setForgotError("");
    try{
      await apiPost("/api/auth/session",{action:"send_reset_code",email:forgotEmail.trim().toLowerCase()},{auth:false});
    }catch(e){
      // Always advance even on failure — we don't reveal if email exists
    }
    // Always advance — we don't reveal if email exists
    setSignInStep("forgot_code");
    setForgotSuccess("If that email exists, a code was sent. Check your inbox.");
    setForgotLoading(false);
  };

  const forgotVerifyCode=async()=>{
    if(forgotCode.length!==6){setForgotError("Enter the 6-digit code from your email");return;}
    setForgotLoading(true);setForgotError("");setForgotSuccess("");
    try{
      const json=await apiPost("/api/auth/session",{action:"verify_reset_code",email:forgotEmail.trim().toLowerCase(),code:forgotCode},{auth:false});
      if(json.reset_token){
        setForgotResetToken(json.reset_token);
        setSignInStep("forgot_newpin");
        setForgotError("");
      }else{
        setForgotError(json.error||(json.attempts_remaining!==undefined?`Wrong code. ${json.attempts_remaining} attempts left`:"Invalid code"));
      }
    }catch(e){
      if(e instanceof ApiError){
        const body=e.body||{};
        setForgotError(body.error||(body.attempts_remaining!==undefined?`Wrong code. ${body.attempts_remaining} attempts left`:"Invalid code"));
      }else setForgotError("Connection failed. Try again.");
    }
    setForgotLoading(false);
  };

  const forgotSetPin=async()=>{
    if(forgotNewPin.length!==6){setForgotError("PIN must be 6 digits");return;}
    if(forgotNewPin!==forgotConfirmPin){setForgotError("PINs don't match");return;}
    setForgotLoading(true);setForgotError("");
    try{
      const json=await apiPost("/api/auth/session",{action:"set_new_pin",email:forgotEmail.trim().toLowerCase(),resetToken:forgotResetToken,newPin:forgotNewPin},{auth:false});
      if(json.success){
        setForgotSuccess("PIN updated! Sign in with your new PIN.");
        setSignInStep("pin");
        setSignInEmail(forgotEmail);
        setSignInPin("");
        setForgotNewPin("");setForgotConfirmPin("");setForgotResetToken("");
      }else{
        setForgotError(json.error||"Failed to update PIN");
      }
    }catch(e){
      setForgotError(e instanceof ApiError?(e.body?.error||"Failed to update PIN"):"Connection failed. Try again.");
    }
    setForgotLoading(false);
  };

  // ─── Email verification state (post-signup) ───
  const [verifyStep,setVerifyStep]=useState(null); // null | "pending" | "done"
  const [verifyCode,setVerifyCode]=useState("");
  const [verifyLoading,setVerifyLoading]=useState(false);
  const [verifyError,setVerifyError]=useState("");
  const [verifyCooldown,setVerifyCooldown]=useState(0);

  const verifySendCode=async(emailAddr)=>{
    try{
      await apiPost("/api/auth/session",{action:"send_verify_code",email:emailAddr||profile.email},{auth:false});
    }catch(e){}
  };

  const verifyConfirmCode=async()=>{
    if(verifyCode.length!==6){setVerifyError("Enter the 6-digit code");return;}
    setVerifyLoading(true);setVerifyError("");
    try{
      const json=await apiPost("/api/auth/session",{action:"confirm_verify_code",email:profile.email,code:verifyCode},{auth:false});
      if(!_mountedRef.current)return;
      if(json.verified||json.already_verified){
        setVerifyStep("done");
        LS.set("ft-email-verified","true");
        const t=setTimeout(()=>{if(_mountedRef.current)d({type:"ONBOARDED"});},1200);
        _timersRef.current.push(t);
      }else{
        setVerifyError(json.error||"Incorrect code");
      }
    }catch(e){
      if(!_mountedRef.current)return;
      setVerifyError(e instanceof ApiError?(e.body?.error||"Incorrect code"):"Connection failed.");
    }
    if(_mountedRef.current)setVerifyLoading(false);
  };

  const verifyResend=async()=>{
    if(verifyCooldown>0)return;
    await verifySendCode(profile.email);
    if(!_mountedRef.current)return;
    setVerifyCooldown(60);
    const t=setInterval(()=>setVerifyCooldown(c=>{if(c<=1){clearInterval(t);return 0;}return c-1;}),1000);
    _timersRef.current.push(t);
  };

  const signIn=async()=>{
    if(signInStep==="email"){
      if(!signInEmail.trim()||!signInEmail.includes("@")){setSignInError("Enter a valid email");return;}
      setSignInLoading(true);setSignInError("");
      try{
        AuthToken.init(signInEmail);
        const deviceId=uid();LS.set("ft-device-id",deviceId);
        // First pull without PIN to check if account exists and needs PIN
        const result=await CloudSync.pull(signInEmail.trim().toLowerCase(),deviceId,null);
        if(result?.pin_required){
          // Account has PIN — go to PIN step (server blocked data)
          setSignInStep("pin");
        }else if(result&&result.success&&result.data){
          // No PIN set (old account) — server returned data directly
          applyRestore(result.data,signInPin||null);
        }else{
          setSignInError(result?.error||"No account found with this email.");
        }
      }catch(e){
        console.error("Sign-in error:",e);
        setSignInError("Something went wrong. Please try again.");
      }
      setSignInLoading(false);
      return;
    }

    // PIN step — validate server-side
    if(signInPin.length!==6){setSignInError("Enter your 6-digit PIN");return;}
    setSignInLoading(true);setSignInError("");
    try{
      const deviceId=LS.get("ft-device-id");
      const result=await CloudSync.pull(signInEmail.trim().toLowerCase(),deviceId,signInPin);
      if(result?.wrong_pin){
        setSignInError(`Wrong PIN. ${result.attempts_remaining} attempt${result.attempts_remaining!==1?"s":""} remaining.`);
        setSignInPin("");
        if(result.locked)setSignInError("Too many attempts. Account locked for 15 minutes.");
      }else if(result?.locked){
        setSignInError(`Account locked. Try again in ${result.retry_minutes} minutes.`);
      }else if(result&&result.success&&result.data){
        applyRestore(result.data,signInPin||null);
      }else{
        setSignInError("Could not restore. Try again.");
      }
    }catch(e){
      console.error("Sign-in PIN error:",e);
      setSignInError("Something went wrong. Please try again.");
    }
    setSignInLoading(false);
  };

  const applyRestore=(cloud,pinUsed)=>{
    const merged={
      workouts:cloud.workouts||[],nutrition:cloud.nutrition||[],body:cloud.body||[],
      photos:cloud.photos||[],checkins:(cloud.checkins||[]).filter(c=>!c.deleted),milestones:cloud.milestones||[],
      // Settings: prefer the cloud record, but fall back to existing LOCAL values (not
      // hardcoded defaults) so signing into a sparsely-synced account doesn't overwrite a
      // user's current goals/units/schedule/profile with defaults.
      // The built-in catalogue always survives a restore. This used to take the cloud
      // list wholesale, and the two clients disagree about what that column means: the
      // web stores its FULL catalogue there, iOS stores CUSTOM exercises only. So any
      // iOS push shrank the column to a handful of rows — or to [], which is truthy in
      // JS and so won this `||` outright — and the next web restore adopted it as the
      // entire exercise database, emptying the picker and the logger. Defaults first,
      // then anything custom from either client, deduped by id.
      exercises:(()=>{
        const pulled=Array.isArray(cloud.exercises)?cloud.exercises:(LS.get("ft-ex")||[]);
        const builtIn=new Set(defaultExercises.map(e=>e.id));
        const extra=(Array.isArray(pulled)?pulled:[]).filter(e=>e&&e.id&&!builtIn.has(e.id));
        return [...defaultExercises,...extra];
      })(),
      goals:cloud.goals||LS.get("ft-g")||{cal:2400,protein:180,carbs:250,fat:70,goalWeight:175},
      activeProgram:activeProgramFromWire(cloud,
        String(LS.get("ft-profile")?.email||"").trim().toLowerCase()===signInEmail.trim().toLowerCase()
          ? LS.get("ft-active-program") : null),
      schedule:cloud.schedule||LS.get("ft-sched")||defaultSchedule,
      units:cloud.units||LS.get("ft-units")||"lbs",
      profile:{...(cloud.profile||LS.get("ft-profile")||{}),email:(cloud.profile?.email||signInEmail.trim().toLowerCase())},
    };
    d({type:"IMPORT",data:merged});
    d({type:"SET_PROFILE",profile:merged.profile});
    d({type:"UNITS",u:merged.units});
    if(merged.goals)d({type:"GOALS",g:merged.goals});
    if(merged.schedule)d({type:"SET_SCHEDULE",schedule:merged.schedule});
    // Identify user in Sentry
    const pEmail=merged.profile?.email||signInEmail.trim().toLowerCase();
    const pName=`${merged.profile?.firstName||merged.profile?.first_name||""} ${merged.profile?.lastName||merged.profile?.last_name||""}`.trim()||merged.profile?.username||"";
    SentryUtil.identify(pEmail,pName);
    SentryUtil.breadcrumb("User signed in","auth",{method:pinUsed?"pin":"no-pin"});
    // vault_pin no longer restored from server (kept local-only)
    if(cloud.phases)d({type:"SET_PHASES",phases:cloud.phases});
    if(cloud.injuries)d({type:"SET_INJURIES",injuries:cloud.injuries});
    if(cloud.privacy)LS.set("ft-privacy",cloud.privacy);
    if(cloud.supplements)LS.set("ft-supplements",cloud.supplements);
    if(cloud.accountability)LS.set("ft-accountability",cloud.accountability);
    // Restore gamification — only write keys that aren't already more up-to-date locally
    if(cloud.gamification){
      const g=cloud.gamification;
      // XP bonus: merge — take whichever total is higher (local may have earned more since last sync)
      const localBonus=LS.get("ft-xp-bonus")||{total:0,log:[]};
      if((g.xp_bonus?.total||0)>(localBonus.total||0))LS.set("ft-xp-bonus",g.xp_bonus);
      // Missions: merge by day — server wins for past days, local wins for today
      const localMissions=LS.get("ft-missions-completed")||{};
      const mergedMissions={...g.missions_completed,...localMissions};
      LS.set("ft-missions-completed",mergedMissions);
      // Shields: take max
      const localShields=LS.get("ft-streak-shields")||0;
      if((g.streak_shields||0)>localShields)LS.set("ft-streak-shields",g.streak_shields);
      if(g.shield_awarded_streak)LS.set("ft-shield-awarded-streak",g.shield_awarded_streak);
      if(g.shield_used_date&&!LS.get("ft-shield-used-date"))LS.set("ft-shield-used-date",g.shield_used_date);
      // Level: take max (don't regress celebration trigger)
      const localLevel=LS.get("ft-last-known-level")||1;
      if((g.last_known_level||1)>localLevel)LS.set("ft-last-known-level",g.last_known_level);
      // Badge dates: merge — keep earliest earned date per badge
      const localBadgeDates=LS.get("ft-badge-dates")||{};
      const mergedBadgeDates={...g.badge_dates};
      for(const[k,v]of Object.entries(localBadgeDates)){if(!mergedBadgeDates[k]||v<mergedBadgeDates[k])mergedBadgeDates[k]=v;}
      LS.set("ft-badge-dates",mergedBadgeDates);
      // Duels + rivals + wars: server wins (most recent sync is source of truth)
      if(g.duels?.length>0&&!(LS.get("ft-duels")?.length>0))LS.set("ft-duels",g.duels);
      if(g.rivals?.length>0&&!(LS.get("ft-rivals")?.length>0))LS.set("ft-rivals",g.rivals);
      if(g.war_wins>0&&!LS.get("ft-war-wins"))LS.set("ft-war-wins",g.war_wins);
      if(g.war_streak>0&&!LS.get("ft-war-streak"))LS.set("ft-war-streak",g.war_streak);
      if(g.comeback_used&&!LS.get("ft-comeback-used"))LS.set("ft-comeback-used",g.comeback_used);
      if(g.duel_record){const local=LS.get("ft-duel-record")||{wins:0,losses:0,ties:0};
        if((g.duel_record.wins||0)>(local.wins||0))LS.set("ft-duel-record",g.duel_record);}
    }
    // Create server session for this device
    if(pinUsed)SessionManager.create(signInEmail.trim().toLowerCase(),pinUsed,LS.get("ft-device-id"));
    LS.set("ft-guide-done",true);
    d({type:"ONBOARDED"});
    SuccessToastCtrl.show(`Welcome back! Restored ${(cloud.workouts||[]).length} workouts`);
  };

  const [showInstallGuide,setShowInstallGuide]=useState(false);
  const isIOS=detectIOS(); // also catches iPadOS, which reports a Macintosh user agent
  const isAndroid=/Android/.test(navigator.userAgent);
  const isStandalone=detectStandalone();

  // ═══════════════════════════════════════════════════════════════════════════
  // Rendering
  // ═══════════════════════════════════════════════════════════════════════════

  const pick = (setter) => (v) => { Haptic.light(); setter(v); };

  const closeSignIn = () => {
    setShowSignIn(false); setSignInError(""); setSignInStep("email"); setSignInPin("");
    setForgotError(""); setForgotSuccess(""); setForgotCode(""); setForgotNewPin(""); setForgotConfirmPin("");
  };

  // ─── Email verification (after sign-up) ───
  if (verifyStep === "pending" || verifyStep === "done") {
    const done = verifyStep === "done";
    return (
      <AuthShell>
        <AuthTitle icon={done ? Icons.check : Icons.zap} title={done ? "You're verified!" : "Check your email"}>
          {done ? `Taking you to ${appName()}…` : <>We sent a 6-digit code to<br /><span style={{ color: V.accent, fontWeight: 700 }}>{profile.email}</span></>}
        </AuthTitle>
        {!done && (<>
          <CodeInput value={verifyCode} onChange={v => { setVerifyCode(v); setVerifyError(""); }} autoFocus autoComplete="one-time-code"
            onEnter={verifyConfirmCode} state={verifyError ? "error" : verifyCode.length === 6 ? "ok" : undefined} ariaLabel="Verification code" />
          <Notice tone="error" center style={{ marginBottom: 10 }}>{verifyError}</Notice>
          <Btn full onClick={verifyConfirmCode} disabled={verifyLoading || verifyCode.length !== 6}>
            {verifyLoading ? "Verifying…" : "Verify Email"}
          </Btn>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 14 }}>
            <LinkBtn onClick={verifyResend} disabled={verifyCooldown > 0}>{verifyCooldown > 0 ? `Resend in ${verifyCooldown}s` : "Resend code"}</LinkBtn>
            <span style={{ color: V.text3, fontSize: 12 }}>·</span>
            <LinkBtn color={V.text3} onClick={() => {
              // Record WHEN the skip happened so the app can re-prompt verification later.
              // Do NOT treat "skipped" as a permanent free pass.
              LS.set("ft-email-verified", { state: "skipped", at: new Date().toISOString() });
              d({ type: "ONBOARDED" });
            }}>Skip for now</LinkBtn>
          </div>
        </>)}
      </AuthShell>
    );
  }

  // ─── Sign in / PIN reset ───
  if (showSignIn) {
    const busy = signInLoading || forgotLoading;
    const primary = signInStep === "forgot" ? forgotSendCode : signInStep === "forgot_code" ? forgotVerifyCode
      : signInStep === "forgot_newpin" ? forgotSetPin : signIn;
    const primaryLabel = busy ? "…" : signInStep === "forgot" ? "Send Code" : signInStep === "forgot_code" ? "Verify Code"
      : signInStep === "forgot_newpin" ? "Save PIN" : "Sign In";
    const primaryReady = signInStep === "email" ? !!signInEmail.trim() : signInStep === "pin" ? signInPin.length === 6
      : signInStep === "forgot" ? !!forgotEmail.trim() : signInStep === "forgot_code" ? forgotCode.length === 6
      : forgotNewPin.length === 6 && forgotConfirmPin.length === 6;
    const back = signInStep === "email" ? closeSignIn
      : signInStep === "pin" ? () => { setSignInStep("email"); setSignInPin(""); setSignInError(""); }
      : signInStep === "forgot" ? () => { setSignInStep("pin"); setForgotError(""); }
      : () => { setSignInStep("forgot"); setForgotError(""); setForgotSuccess(""); };

    return (
      <AuthShell onBack={back}>
        {signInStep === "email" && (<>
          <AuthTitle title="Welcome back">Sign in with the email you used when you created your account. Your workouts, nutrition and progress will be restored on this device.</AuthTitle>
          <TextInput label="Email" type="email" value={signInEmail} onChange={setSignInEmail} placeholder="you@email.com"
            autoComplete="email" autoFocus inputMode="email" onEnter={signIn} error={signInError} />
        </>)}
        {signInStep === "pin" && (<>
          <AuthTitle icon={Icons.hash} title="Enter your PIN">The 6-digit PIN you created for<br /><span style={{ color: V.text, fontWeight: 700 }}>{signInEmail.trim().toLowerCase()}</span></AuthTitle>
          <CodeInput value={signInPin} onChange={v => { setSignInPin(v); setSignInError(""); }} secret autoFocus autoComplete="current-password"
            name="pin" onEnter={signIn} state={signInError ? "error" : undefined} ariaLabel="Account PIN" />
          <Notice tone="error" center style={{ marginBottom: 8 }}>{signInError}</Notice>
        </>)}
        {signInStep === "forgot" && (<>
          <AuthTitle icon={Icons.refresh} title="Reset your PIN">Enter your email and we'll send a 6-digit reset code.</AuthTitle>
          <TextInput label="Email" type="email" value={forgotEmail} onChange={setForgotEmail} placeholder="you@email.com"
            autoComplete="email" autoFocus inputMode="email" onEnter={forgotSendCode} error={forgotError} />
        </>)}
        {signInStep === "forgot_code" && (<>
          <AuthTitle icon={Icons.zap} title="Enter reset code">{forgotSuccess || "Check your email for a 6-digit code."}</AuthTitle>
          <CodeInput value={forgotCode} onChange={v => { setForgotCode(v); setForgotError(""); }} autoFocus autoComplete="one-time-code"
            onEnter={forgotVerifyCode} state={forgotError ? "error" : undefined} ariaLabel="Reset code" />
          <Notice tone="error" center style={{ marginBottom: 8 }}>{forgotError}</Notice>
        </>)}
        {signInStep === "forgot_newpin" && (<>
          <AuthTitle icon={Icons.hash} title="Set a new PIN">Choose a new 6-digit PIN. You'll use it to sign in on new devices.</AuthTitle>
          <CodeInput label="New PIN" value={forgotNewPin} onChange={v => { setForgotNewPin(v); setForgotError(""); }} secret autoFocus autoComplete="new-password" name="new-pin" />
          <CodeInput label="Confirm PIN" value={forgotConfirmPin} onChange={v => { setForgotConfirmPin(v); setForgotError(""); }} secret autoComplete="new-password" name="confirm-pin"
            onEnter={forgotSetPin} state={forgotConfirmPin.length === 6 ? (forgotConfirmPin === forgotNewPin ? "ok" : "error") : undefined} />
          <Notice tone={forgotConfirmPin.length === 6 && forgotConfirmPin !== forgotNewPin ? "error" : "ok"} center style={{ marginBottom: 8 }}>
            {forgotError || (forgotConfirmPin.length === 6 ? (forgotConfirmPin === forgotNewPin ? "PINs match" : "PINs don't match") : "")}
          </Notice>
        </>)}

        <Btn full onClick={primary} disabled={busy || !primaryReady} s={{ marginTop: 4 }}>{primaryLabel}</Btn>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
          {signInStep === "pin" && (<>
            <LinkBtn onClick={() => { setSignInStep("forgot"); setForgotEmail(signInEmail); setForgotError(""); setForgotSuccess(""); }}>Forgot PIN?</LinkBtn>
            <span style={{ color: V.text3, fontSize: 12 }}>·</span>
            <LinkBtn color={V.text3} onClick={() => { setSignInStep("email"); setSignInPin(""); setSignInError(""); }}>Use a different email</LinkBtn>
          </>)}
          {signInStep === "email" && (<>
            <span style={{ fontSize: 13, color: V.text3 }}>New here?</span>
            <LinkBtn onClick={closeSignIn}>Create an account</LinkBtn>
          </>)}
        </div>
      </AuthShell>
    );
  }

  // ─── Landing page (step 0) ───
  if (step === 0) {
    return (
      <div style={{ height: "100%", overflowY: "auto", WebkitOverflowScrolling: "touch", background: `${V.ambient}, ${V.bg}`, color: V.text, fontFamily: V.font }}>
        <div style={{ minHeight: "100%", display: "flex", flexDirection: "column", maxWidth: isMobile ? 480 : 1080, margin: "0 auto",
          padding: "max(12px, env(safe-area-inset-top)) 24px max(28px, env(safe-area-inset-bottom))" }}>

          {/* Top bar */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 52 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}><LogoMark size={32} /><Wordmark size={19} /></div>
            <LinkBtn onClick={() => setShowSignIn(true)} style={{ padding: "8px 12px", borderRadius: 10, background: V.hoverBg, border: `1px solid ${V.cardBorder}` }}>Sign in</LinkBtn>
          </div>

          {/* Hero */}
          <div style={{ flex: isMobile ? "none" : 1, display: isMobile ? "flex" : "grid", flexDirection: "column", gridTemplateColumns: "1.05fr 1fr", gap: isMobile ? 32 : 56,
            alignItems: "center", alignContent: "center", padding: isMobile ? "28px 0 12px" : "44px 0 36px" }}>
            <div className="ob-rise">
              <Eyebrow color={V.accent} style={{ marginBottom: 14 }}>Welcome to {appName()}</Eyebrow>
              <h1 style={{ margin: 0, fontSize: isMobile ? 40 : 56, fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 1.02, color: V.text }}>
                Lift. Log.<br />
                <span style={{ background: `linear-gradient(90deg,${V.accent},${V.accent2})`, WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent", color: "transparent" }}>Level up.</span>
              </h1>
              <p style={{ fontSize: isMobile ? 15 : 17, color: V.text2, lineHeight: 1.6, margin: "18px 0 24px", maxWidth: 460 }}>
                A free, offline-first tracker for people who care about the numbers. Workouts, macros, bodyweight and PRs in one place, with no ads and no subscription.
              </p>
              <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 10 }}>
                <Btn full={isMobile} onClick={() => { Haptic.light(); setStep(1); }} s={{ minHeight: 52, fontSize: 15, padding: "14px 24px" }}>
                  Get Started {Icons.chevRight({ size: 18, color: V.mode === "dark" ? V.bg : "#fff", strokeWidth: 2.6 })}
                </Btn>
                <Btn v="secondary" full={isMobile} onClick={() => setShowSignIn(true)} s={{ minHeight: 52, fontSize: 15, padding: "14px 22px", color: V.text }}>
                  Welcome back? Sign in
                </Btn>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px", marginTop: 22 }}>
                {TRUST.map(t => (
                  <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: V.text3 }}>
                    {Icons.check({ size: 13, color: V.accent, strokeWidth: 3 })}{t}
                  </span>
                ))}
              </div>
            </div>
            <div className="ob-rise ob-delay-1" style={{ width: "100%" }}><AppPreview /></div>
          </div>

          {/* Features */}
          <div className="ob-rise ob-delay-2" style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)", gap: 10, marginTop: isMobile ? 8 : 4 }}>
            {FEATURES.map(f => <FeatureTile key={f.title} icon={f.icon} color={tone(f.color)} title={f.title} desc={f.desc} />)}
          </div>

          {/* Install as app */}
          {!isStandalone && (
            <button type="button" onClick={() => setShowInstallGuide(true)} className="hover-lift" style={{
              display: "flex", alignItems: "center", gap: 12, width: "100%", marginTop: 14, padding: "12px 14px", borderRadius: 16,
              background: "transparent", border: `1px dashed ${V.accent}55`, cursor: "pointer", textAlign: "left", fontFamily: V.font, WebkitTapHighlightColor: "transparent",
            }}>
              <div style={{ width: 38, height: 38, borderRadius: 12, background: `${V.accent}14`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {Icons.download({ size: 17, color: V.accent })}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: V.text }}>Install as an app</div>
                <div style={{ fontSize: 11, color: V.text3, marginTop: 1 }}>Add {appName()} to your home screen for offline use and reminders</div>
              </div>
              {Icons.chevRight({ size: 16, color: V.text3 })}
            </button>
          )}
          {showInstallGuide && <InstallGuide isIOS={isIOS} isAndroid={isAndroid} onClose={() => setShowInstallGuide(false)} />}

          <div style={{ marginTop: 22, textAlign: "center", fontSize: 11, color: V.text3 }}>
            Made for lifters · Powerlifting, bodybuilding and everything in between
          </div>
        </div>
      </div>
    );
  }

  // ─── Wizard steps 1–6 ───
  const splitColor = (k) => k === "PPL" ? V.accent : k === "Bro" ? V.warn : k === "Upper/Lower" ? V.accent2 : k === "Full Body" ? V.purple : V.text2;
  const macroKcal = (parseInt(goals.protein) || 0) * 4 + (parseInt(goals.carbs) || 0) * 4 + (parseInt(goals.fat) || 0) * 9;
  const calTarget = parseInt(goals.cal) || 0;
  const macroDiff = macroKcal - calTarget;
  const macroOff = calTarget > 0 && Math.abs(macroDiff) > calTarget * 0.1;
  const pinsMatch = accountPin.length === 6 && confirmPin.length === 6 && accountPin === confirmPin;
  const pinsMismatch = accountPin.length === 6 && confirmPin.length === 6 && accountPin !== confirmPin;

  const canNext = [null, true, !!primaryGoal, true, bodyValid, true, !!profileValid, pinsMatch && !sending][step];
  const next = () => { Haptic.light(); if (step === 7) finish(); else setStep(s => s + 1); };

  const stepContent = () => {
    switch (step) {
      case 1: return (<>
        <StepHeader icon={Icons.scale} title="Units" subtitle="Choose your units for bodyweight and lifts. You can switch later and your history converts automatically." />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {[["lbs", "Pounds", "225 lb bench"], ["kg", "Kilograms", "100 kg bench"]].map(([u, name, ex]) => (
            <OptionCard key={u} center selected={units === u} onClick={() => pick(setUnits)(u)} ariaLabel={`${u} — ${name}`}>
              <div style={{ fontSize: 30, fontWeight: 900, letterSpacing: "-0.03em", color: units === u ? V.accent : V.text, fontFamily: V.mono }}>{u.toUpperCase()}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: V.text, marginTop: 4 }}>{name}</div>
              <div style={{ fontSize: 11, color: V.text3, marginTop: 2 }}>e.g. {ex}</div>
            </OptionCard>
          ))}
        </div>
      </>);

      case 2: return (<>
        <StepHeader icon={Icons.target} color={V.warn} title="Primary goal" subtitle="What are you training for? This sets your calorie target and shapes coaching suggestions." />
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {GOALS.map(g => (
            <OptionCard key={g.id} selected={primaryGoal === g.id} onClick={() => pick(setPrimaryGoal)(g.id)} icon={g.icon} color={tone(g.color)} title={g.id} desc={g.desc} />
          ))}
        </div>
      </>);

      case 3: return (<>
        <StepHeader icon={Icons.calendar} color={V.accent2} title="Your schedule" subtitle="How often can you train? We'll pick a split that fits, and you can change any day later." />
        <Eyebrow style={{ marginBottom: 8 }}>Days per week</Eyebrow>
        <Segmented options={[2, 3, 4, 5, 6, 7].map(n => ({ value: n, label: String(n) }))} value={trainingDays} onChange={v => { Haptic.light(); setTrainingDays(v); }} />
        <Eyebrow style={{ margin: "16px 0 8px" }}>Session length</Eyebrow>
        <Segmented small options={SESSION_LENGTHS.map(m => ({ value: m, label: `${m} min` }))} value={sessionLength} onChange={v => { Haptic.light(); setSessionLength(v); }} />
        <Eyebrow style={{ margin: "16px 0 8px" }}>When do you usually train?</Eyebrow>
        <ChipRow options={TRAINING_TIMES} value={trainingTime} onChange={v => { Haptic.light(); setTrainingTime(v); }} />

        <SectionLabel title="Your split" hint={splitTouched ? "Your pick — the recommendation is still available below." : `Recommended for ${trainingDays} days a week${profile.fitnessLevel ? ` at ${profile.fitnessLevel.toLowerCase()} level` : ""}.`} />
        <OptionCard selected title={split} desc={splits[split].desc} icon={split === "Custom" ? Icons.sliders : Icons.calendar} color={splitColor(split)}>
          {split !== "Custom" && <WeekStrip days={weekPlan} legend={splits[split].desc} />}
        </OptionCard>
        <div style={{ textAlign: "center", marginTop: 6 }}>
          <LinkBtn onClick={() => setShowSplits(v => !v)}>{showSplits ? "Hide other splits" : "Choose a different split"}</LinkBtn>
        </div>
        {showSplits && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 6 }}>
            {Object.entries(splits).filter(([k]) => k !== split).map(([k, v]) => (
              <OptionCard key={k} selected={false} onClick={() => { Haptic.light(); setSplit(k); setSplitTouched(k !== recommendedSplit); setShowSplits(false); }} title={k}
                desc={k === "Custom" ? v.desc : undefined} icon={k === "Custom" ? Icons.sliders : Icons.calendar} color={splitColor(k)}>
                {k !== "Custom" && <WeekStrip days={buildWeek(k, trainingDays)} legend={v.desc} />}
              </OptionCard>
            ))}
          </div>
        )}
      </>);

      case 4: return (<>
        <StepHeader icon={Icons.activity} color={V.purple} title="About your body" subtitle="Used to calculate your calorie and macro targets. Nothing here is shared with other users." />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div style={{ marginBottom: 12 }}>
            <Eyebrow style={{ marginBottom: 6 }}>Sex<span style={{ color: V.accent }}> *</span></Eyebrow>
            <Segmented options={["Male", "Female"]} value={profile.sex} onChange={v => { Haptic.light(); setProfile(p => ({ ...p, sex: v })); }} />
          </div>
          <TextInput label="Date of birth" required type="date" value={profile.dob} onChange={v => setProfile(p => ({ ...p, dob: v }))} autoComplete="bday"
            error={profile.dob && !ageValid ? "You must be 16 or older" : ""} />
        </div>
        {units === "kg" ? (
          <Field label="Height" type="number" value={heightCm} onChange={setHeightCm} unit="cm" placeholder="178" />
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field label="Height" type="number" value={heightFt} onChange={setHeightFt} unit="ft" placeholder="5" />
            <Field label="&nbsp;" type="number" value={heightIn} onChange={setHeightIn} unit="in" placeholder="10" />
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="Current weight" type="number" value={bw} onChange={setBw} unit={units} placeholder={units === "kg" ? "82" : "180"} />
          <Field label="Goal weight" type="number" value={goals.goalWeight} onChange={v => setGoals(g => ({ ...g, goalWeight: v }))} unit={units} placeholder={units === "kg" ? "80" : "175"} />
        </div>
        <SelectField label="Activity outside the gym" value={profile.activityLevel} onChange={v => setProfile(p => ({ ...p, activityLevel: v }))}
          options={["Sedentary", "Lightly Active", "Moderate", "Very Active", "Extremely Active"]} placeholder="Select…" />

        <SectionLabel title="Daily targets" hint={suggested ? "Calculated from your body and goal. Edit anything you like." : "Add your height, weight, sex and birthday to get suggested targets."} />
        {suggested && (
          <Callout icon={Icons.zap} title={`Suggested for ${primaryGoal || "your goal"}`} style={{ marginBottom: 12 }}>
            Maintenance is about <strong style={{ color: V.text }}>{suggested.tdee.toLocaleString()} kcal</strong>
            {suggested.adjust !== 0 && <> · {suggested.adjust > 0 ? "surplus" : "deficit"} of {Math.abs(suggested.adjust)} kcal</>}
            {" "}· protein {suggested.perLb} g per lb of bodyweight.
            {targetsTouched && <> <LinkBtn onClick={applySuggested} style={{ padding: 0, fontSize: 12 }}>Reset to suggested</LinkBtn></>}
          </Callout>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="Calories" type="number" value={goals.cal} onChange={editGoal("cal")} unit="kcal" />
          <Field label="Protein" type="number" value={goals.protein} onChange={editGoal("protein")} unit="g" />
          <Field label="Carbs" type="number" value={goals.carbs} onChange={editGoal("carbs")} unit="g" />
          <Field label="Fat" type="number" value={goals.fat} onChange={editGoal("fat")} unit="g" />
        </div>
        <div style={{ padding: "12px 14px", borderRadius: 14, background: V.cardGrad, border: `1px solid ${V.cardBorder}` }}>
          <div aria-hidden="true" style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", background: V.divider, marginBottom: 10 }}>
            {[["protein", 4, V.accent], ["carbs", 4, V.accent2], ["fat", 9, V.purple]].map(([k, mult, c]) => (
              <div key={k} style={{ width: `${macroKcal ? ((parseInt(goals[k]) || 0) * mult / macroKcal) * 100 : 0}%`, background: c, transition: "width .3s" }} />
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: 12 }}>
              {[["Protein", V.accent], ["Carbs", V.accent2], ["Fat", V.purple]].map(([l, c]) => (
                <span key={l} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: V.text3, fontWeight: 600 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 4, background: c }} />{l}
                </span>
              ))}
            </div>
            <span style={{ fontSize: 12, fontFamily: V.mono, fontWeight: 700, color: macroOff ? V.warn : V.text2 }}>
              {macroKcal.toLocaleString()} kcal from macros
            </span>
          </div>
          {macroOff && <Notice tone="warn" style={{ marginTop: 8 }}>
            Your macros add up to {Math.abs(macroDiff).toLocaleString()} kcal {macroDiff > 0 ? "more" : "less"} than your calorie target.
          </Notice>}
        </div>
      </>);

      case 5: return (<>
        <StepHeader icon={Icons.dumbbell} title="Your training" subtitle="Helps the coach and exercise picker suggest things you can actually do." />
        <Eyebrow style={{ marginBottom: 8 }}>Experience</Eyebrow>
        <Segmented options={["Beginner", "Intermediate", "Advanced"]} value={profile.fitnessLevel} onChange={v => { Haptic.light(); setProfile(p => ({ ...p, fitnessLevel: v })); }} />

        <SectionLabel title="Where do you train?" hint="Filters the exercise picker to equipment you have. You can always see everything." />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {EQUIPMENT.map(e => (
            <OptionCard key={e.id} center selected={equipment === e.id} onClick={() => pick(setEquipment)(e.id)} icon={e.icon} color={tone(e.color)} title={e.label} desc={e.desc} />
          ))}
        </div>

        <SectionLabel title="Current lifts" hint="Optional. A weight you could lift once. Seeds your strength score and PR baseline from day one." />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          <Field label="Bench" type="number" value={lifts.bench} onChange={v => setLifts(l => ({ ...l, bench: v }))} unit={units} />
          <Field label="Squat" type="number" value={lifts.squat} onChange={v => setLifts(l => ({ ...l, squat: v }))} unit={units} />
          <Field label="Deadlift" type="number" value={lifts.deadlift} onChange={v => setLifts(l => ({ ...l, deadlift: v }))} unit={units} />
        </div>

        <SectionLabel title="Anything to work around?" hint="Optional. Flags exercises that load the area so the coach can suggest alternatives." />
        <MultiChipRow options={JOINTS.map(j => j.label)} values={injuries.map(id => JOINTS.find(j => j.id === id)?.label)} noneLabel="None"
          onChange={labels => { Haptic.light(); setInjuries(labels.map(l => JOINTS.find(j => j.label === l)?.id).filter(Boolean)); }} />
      </>);

      case 6: return (<>
        <StepHeader icon={Icons.users} title="Your profile" subtitle="Tell us a little about yourself. Required fields are marked with *." />
        <SectionLabel title="About you" style={{ marginTop: 0 }} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <TextInput label="First name" required value={profile.firstName} onChange={v => setProfile(p => ({ ...p, firstName: v }))} placeholder="Alex" autoComplete="given-name"
            error={profile.firstName && containsProfanity(profile.firstName) ? "Please use an appropriate name" : ""} />
          <TextInput label="Last name" required value={profile.lastName} onChange={v => setProfile(p => ({ ...p, lastName: v }))} placeholder="Smith" autoComplete="family-name"
            error={profile.lastName && containsProfanity(profile.lastName) ? "Please use an appropriate name" : ""} />
        </div>
        <TextInput label="Email" required type="email" inputMode="email" value={profile.email} onChange={v => setProfile(p => ({ ...p, email: v }))} placeholder="you@email.com" autoComplete="email"
          error={profile.email && !emailValid ? "Enter a valid email address" : ""} hint="We'll send a verification code here." />
        <TextInput label="Nickname" value={profile.nickname} onChange={v => setProfile(p => ({ ...p, nickname: v }))} placeholder="What should we call you?" autoComplete="nickname"
          error={profile.nickname && containsProfanity(profile.nickname) ? "Please use an appropriate nickname" : ""} />

        <SectionLabel title="Location" hint="Used for local leaderboards and friend suggestions." />
        <SelectField label="State / Region" required value={profile.state} onChange={v => setProfile(p => ({ ...p, state: v }))} options={US_STATES} placeholder="Select state…" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <TextInput label="City" value={profile.city} onChange={v => setProfile(p => ({ ...p, city: v }))} placeholder="Austin" autoComplete="address-level2" />
          <TextInput label="Zip code" inputMode="numeric" value={profile.zipCode} onChange={v => setProfile(p => ({ ...p, zipCode: v.replace(/\D/g, "").slice(0, 5) }))} placeholder="78701" autoComplete="postal-code" />
        </div>

        <SectionLabel title="Food" hint="Shapes the meal plan generator. Optional." />
        <Eyebrow style={{ marginBottom: 8 }}>Diet</Eyebrow>
        <ChipRow options={DIETS} value={diet} onChange={v => { Haptic.light(); setDiet(v); }} />
        <Eyebrow style={{ margin: "14px 0 8px" }}>Allergies or avoid</Eyebrow>
        <MultiChipRow options={ALLERGIES} values={allergies} noneLabel="None" onChange={v => { Haptic.light(); setAllergies(v); }} />

        {/* Consent */}
        <button type="button" role="checkbox" aria-checked={agreed} onClick={() => { Haptic.light(); setAgreed(a => !a); }} style={{
          display: "flex", alignItems: "flex-start", gap: 12, width: "100%", marginTop: 20, padding: "14px", borderRadius: 14, textAlign: "left",
          background: agreed ? V.cardGlow : V.cardGrad, border: `1.5px solid ${agreed ? V.accent : V.cardBorder}`, cursor: "pointer",
          fontFamily: V.font, WebkitTapHighlightColor: "transparent", transition: "border-color .15s, background .2s",
        }}>
          <div aria-hidden="true" style={{ width: 22, height: 22, borderRadius: 7, flexShrink: 0, marginTop: 1, border: `2px solid ${agreed ? V.accent : V.text3}`,
            background: agreed ? V.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center", transition: "all .2s" }}>
            {agreed && Icons.check({ size: 13, color: V.mode === "dark" ? V.bg : "#fff", strokeWidth: 3.2 })}
          </div>
          <div style={{ fontSize: 13, color: V.text2, lineHeight: 1.6 }}>
            I'm 16 or older and agree to the{" "}
            <span onClick={e => { e.stopPropagation(); setShowLegal("tos"); }} style={{ color: V.accent, textDecoration: "underline", fontWeight: 700 }}>Terms of Service</span>
            {" "}and{" "}
            <span onClick={e => { e.stopPropagation(); setShowLegal("privacy"); }} style={{ color: V.accent, textDecoration: "underline", fontWeight: 700 }}>Privacy Policy</span>.
          </div>
        </button>
        {showLegal && (
          <Sheet title={showLegal === "tos" ? "Terms of Service" : "Privacy Policy"} onClose={() => setShowLegal(null)}
            footer={<Btn full onClick={() => setShowLegal(null)}>Close</Btn>}>
            {showLegal === "tos" ? <TOSContent /> : <PrivacyContent />}
          </Sheet>
        )}
      </>);

      case 7: return (<>
        <StepHeader icon={Icons.hash} title="Secure your account" subtitle="Create a 6-digit PIN. You'll use it to sign back in if you reinstall or switch devices." />
        <form onSubmit={e => { e.preventDefault(); if (canNext) next(); }} autoComplete="on">
          <input type="email" name="username" value={profile.email} readOnly autoComplete="username" aria-hidden="true" tabIndex={-1}
            style={{ position: "absolute", opacity: 0, height: 0, width: 0, pointerEvents: "none" }} />
          <CodeInput label="Create PIN" value={accountPin} onChange={v => { setAccountPin(v); setPinError(""); }} secret autoFocus autoComplete="new-password" name="new-pin"
            state={accountPin.length === 6 ? "ok" : undefined} />
          <CodeInput label="Confirm PIN" value={confirmPin} onChange={v => { setConfirmPin(v); setPinError(""); }} secret autoComplete="new-password" name="confirm-pin"
            state={pinsMatch ? "ok" : pinsMismatch ? "error" : undefined} />
        </form>
        <Notice tone={pinError || pinsMismatch ? "error" : "ok"} center style={{ minHeight: 18, marginBottom: 16 }}>
          {pinError || (pinsMismatch ? "PINs don't match" : pinsMatch ? "PINs match" : "")}
        </Notice>
        <Callout icon={Icons.zap} title="Save it to your keychain">
          When your browser offers to save this password, tap <strong style={{ color: V.accent }}>Save</strong>. It will autofill your PIN next time you sign in.
        </Callout>
      </>);

      default: return null;
    }
  };

  return (
    <div style={{ height: "100%", overflowY: "auto", WebkitOverflowScrolling: "touch", background: `${V.ambient}, ${V.bg}`, color: V.text, fontFamily: V.font }}>
      <div style={{ minHeight: "100%", display: "flex", flexDirection: "column", maxWidth: isMobile ? 480 : 600, margin: "0 auto" }}>

        {/* Sticky header: back + progress */}
        <div style={{ position: "sticky", top: 0, zIndex: 5, background: V.bg,
          padding: "max(8px, env(safe-area-inset-top)) 20px 12px", borderBottom: `1px solid ${V.divider}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 44, marginBottom: 6 }}>
            <button type="button" onClick={() => { Haptic.light(); setStep(s => s - 1); }} aria-label="Back" style={{ background: "none", border: "none", display: "flex", alignItems: "center",
              gap: 4, padding: "8px 8px 8px 0", cursor: "pointer", fontFamily: V.font, WebkitTapHighlightColor: "transparent" }}>
              {Icons.chevLeft({ size: 18, color: V.accent })}
              <span style={{ fontSize: 14, color: V.accent, fontWeight: 600 }}>Back</span>
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}><LogoMark size={24} /><Wordmark size={15} /></div>
          </div>
          <ProgressSteps step={step} labels={STEP_LABELS} />
        </div>

        {/* Step body */}
        <div key={step} className="tab-anim" style={{ flex: 1, padding: "24px 20px 20px" }}>
          {stepContent()}
        </div>

        {/* Sticky footer: primary action */}
        <div style={{ position: "sticky", bottom: 0, zIndex: 5, padding: "12px 20px max(18px, env(safe-area-inset-bottom))",
          background: V.bg, borderTop: `1px solid ${V.divider}` }}>
          {step === 4 && !bodyValid && (
            <Notice tone={profile.dob && !ageValid ? "error" : "neutral"} center style={{ marginBottom: 10 }}>
              {profile.dob && !ageValid ? "You must be at least 16 years old to use IRONLOG" : "Select your sex and date of birth to continue"}
            </Notice>
          )}
          {step === 6 && !profileValid && (
            <Notice tone={!nameClean || (profile.dob && !ageValid) || (profile.email && !emailValid) ? "error" : "neutral"} center style={{ marginBottom: 10 }}>
              {getValidationMsg()}
            </Notice>
          )}
          <Btn full onClick={next} disabled={!canNext} s={{ minHeight: 52, fontSize: 15 }}>
            {step === 7 ? (sending ? (
              <span style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
                <span style={{ width: 16, height: 16, borderRadius: 8, border: "2px solid rgba(0,0,0,0.2)", borderTopColor: V.mode === "dark" ? V.bg : "#fff", animation: "spin 0.8s linear infinite" }} />
                Setting up…
              </span>
            ) : <>{Icons.check({ size: 17, color: V.mode === "dark" ? V.bg : "#fff", strokeWidth: 3 })} Complete Setup</>)
              : <>Next {Icons.chevRight({ size: 17, color: V.mode === "dark" ? V.bg : "#fff", strokeWidth: 2.6 })}</>}
          </Btn>
        </div>
      </div>
    </div>
  );
}
